import React, { useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Link, useParams } from 'react-router-dom';
import { NotFound } from '../NotFound';
import { PartyEditResult } from '../../_generatedTypes.g';
import { DocumentTitle } from '../DocumentTitle';
import { RenderErrors } from '../ui/ErrorComponent';
import { canEditPartyText } from '../../selectors';
import { useDispatch } from 'react-redux';
import { getTranslator } from '../../Translator';
import { partyEdited, saveChanges, startEditingParty } from '../../features/party/partyEditSlice';
import { ElectionContainer } from '../election/ElectionContainer';
import { useAppSelector } from '../../hooks';


type PartyEditComponentParams = {
    partyId: string,
};

export const PartyEditComponent = () => {
    const language = useAppSelector(state => state.language);
    const account = useAppSelector(state => state.clientState.Account);
    const parties = useAppSelector(state => state.parties);
    const elections = useAppSelector(state => state.elections);
    const editingPartyById = useAppSelector(state => state.editingPartyById);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);


    const dispatch = useDispatch();
    const t = getTranslator(language);
    const hasDistributionOfSeats = useAppSelector(state => state.clientState.HasDistributionOfSeats);
    const justDutch = useAppSelector(state => state.clientState.JustDutch);

    const partyId = Number(useParams<PartyEditComponentParams>().partyId);

    const isPartyTextEditable = canEditPartyText(account, parties, elections, partyId);
    const editingParty = isPartyTextEditable ? editingPartyById[partyId] : undefined;

    useEffect(() => {
        if (!editingParty) {
            startEditingParty(partyId, parties, dispatch);
        }
    }, [editingParty])

    if (!editingParty) {
        return <NotFound />
    }

    const textAreaRows = 15;

    return <ElectionContainer partyId={partyId}>
        <div>
            <h1>{t.translate(editingParty.Name)}</h1>
            <DocumentTitle title={t.translate(editingParty.Name)} />
            <div className="partyedit">
                <label>
                    {t['Link to more party-specific information (must start with http:// or https://)']}
                    <input
                        className="partyedit__url"
                        type="url"
                        placeholder="https://example.com"
                        value={editingParty.URL || ''}
                        maxLength={4000}
                        onChange={e => dispatch(partyEdited({ partyId, partyChanges: { URL: e.target.value } }))} />
                </label>
                <label>
                    {t['Description (NL, max. 4000 characters)']}
                    <TextareaAutosize
                        lang="NL"
                        className="partyedit__description"
                        onChange={e => dispatch(partyEdited({ partyId, partyChanges: { Description_NL: e.currentTarget.value } }))}
                        minRows={textAreaRows}
                        maxLength={4000}
                        value={editingParty.Description_NL}>
                    </TextareaAutosize>
                </label>
                {!justDutch && <label>
                    {t['Description (EN, max. 4000 characters)']}
                    <TextareaAutosize
                        lang="EN"
                        className="partyedit__description"
                        onChange={e => dispatch(partyEdited({ partyId, partyChanges: { Description_EN: e.currentTarget.value } }))}
                        minRows={textAreaRows}
                        maxLength={4000}
                        value={editingParty.Description_EN}>
                    </TextareaAutosize>
                </label>}
                {hasDistributionOfSeats && <label>
                    <input
                        name="ListOrder"
                        id="ListOrderYes"
                        checked={editingParty.ListOrder}
                        onChange={e => dispatch(partyEdited({ partyId, partyChanges: { ListOrder: true } }))}
                        type="radio" /> {t['List order']}
                    <input
                        name="ListOrder"
                        id="ListOrderNo"
                        checked={!editingParty.ListOrder}
                        onChange={e => dispatch(partyEdited({ partyId, partyChanges: { ListOrder: false } }))}
                        type="radio" /> {t['Majority of votes']}
                </label>}
                <div style={{ marginBottom: "1em" }}></div>
                {(() => {
                    if (editingParty.EditResult == PartyEditResult.Success) {
                        return <div className="statusbanner--ok">{t['Your changes were successfully saved.']}</div>
                    } else if (editingParty.EditResult == PartyEditResult.Saving) {
                        return <div className="statusbanner--ok">{t['Saving...']}</div>
                    } else if (editingParty.EditResult == PartyEditResult.Error) {
                        return <div className="statusbanner--warning">
                            {t['An error occurred during saving.']}
                            <RenderErrors errors={editingParty.ModelErrors} />
                        </div>
                    } else {
                        return null;
                    }
                })()}
                <div className="buttonrow">
                    <button type="button" className="button--important" onClick={() => saveChanges(editingPartyById[partyId], token, dispatch)}>
                        {t['Save']}
                    </button>
                    <Link className="linkbutton" to={`/party/${partyId}`}>{t['Back']}</Link>
                </div>
            </div>
        </div>
    </ElectionContainer>
}
